import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Shield, 
  AlertTriangle, 
  DollarSign, 
  Clock, 
  TrendingDown,
  Check,
  ArrowRight,
  MapPin,
  Building2,
  Truck,
  Users,
  Calculator,
  FileText,
  ChevronDown,
  Loader2,
  ArrowLeft,
  Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackPageView, trackFormSubmit } from '../hooks/useAnalytics';
import { InsuranceCalculator } from './InsuranceCalculator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// US States for insurance signup
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const painPoints = [
  {
    icon: DollarSign,
    title: 'Sky-High Premiums',
    description: 'Traditional NEMT insurance charges flat monthly premiums regardless of how many trips you run. Slow month? You still pay full price.'
  },
  {
    icon: Clock,
    title: 'Paying for Idle Time',
    description: 'Your vehicles sit parked 60% of the time, but you\'re paying insurance as if they\'re on the road 24/7.'
  },
  {
    icon: AlertTriangle,
    title: 'Coverage Gaps',
    description: 'Standard commercial auto policies don\'t understand NEMT. You end up over-insured in some areas, under-insured in others.'
  },
  {
    icon: TrendingDown,
    title: 'No Flexibility',
    description: 'Scaling up or down? Traditional insurers lock you into annual contracts with penalties for changes.'
  }
];

const benefits = [
  'Pay only when your vehicles are actively on trips',
  'Real-time trip tracking through MedexOVA platform',
  'Coverage designed specifically for NEMT operations',
  'No long-term contracts - scale up or down anytime',
  'Instant proof of insurance for each trip',
  'Workers compensation included',
  'Streamlined claims through our platform'
];

const coverageTypes = [
  { name: 'Commercial Auto Liability', included: true },
  { name: 'General Liability', included: true },
  { name: 'Workers Compensation', included: true },
  { name: 'Hired & Non-Owned Auto', included: true },
  { name: 'Medical Payments', included: true },
  { name: 'Uninsured/Underinsured Motorist', included: true }
];

export const InsurancePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    state: '',
    fleet_size: '',
    monthly_trips: '',
    current_software: '',
    has_dispatch_software: '',
    software_name: '',
    interested_in_medexova_software: false,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Track page view on mount
  useEffect(() => {
    trackPageView('/insurance', 'medex_group_insurance');
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.email || !formData.state || !formData.fleet_size) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/products/signup`, {
        ...formData,
        product: 'medex_group_insurance',
        tags: ['insurance', 'pay_as_you_go', `state_${formData.state.toLowerCase().replace(' ', '_')}`],
        source: 'insurance_landing_page'
      });
      
      // Track successful signup
      trackFormSubmit('insurance_signup', 'medex_group_insurance', { state: formData.state });
      
      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-medex-bg flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-neon-green/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-neon-green" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-white mb-4">
            You're On The List!
          </h2>
          <p className="text-slate-400 mb-6">
            We're building coverage in <span className="text-neon-purple font-semibold">{formData.state}</span>. 
            Once we reach 20 providers in your state, we'll activate group coverage and reach out with next steps.
          </p>
          <div className="glass p-4 rounded-lg mb-6 text-left">
            <h4 className="text-sm font-mono text-slate-500 uppercase tracking-wider mb-2">What Happens Next?</h4>
            <ol className="text-sm text-slate-300 space-y-2">
              <li>1. We verify your information</li>
              <li>2. You'll receive updates on {formData.state} enrollment progress</li>
              <li>3. At 20 providers, we bind group coverage</li>
              <li>4. You start saving with Activity-Based</li>
            </ol>
          </div>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Return to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-medex-bg">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-neon-purple/10 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>

          {/* Founding Provider Banner */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 rounded-xl bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-cyan-500/30"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-cyan-400 font-medium">Now Accepting Founding Providers</span>
                <span className="text-slate-400 hidden sm:inline">— Lock in exclusive rates</span>
              </div>
              <Link 
                to="/insurance/founding-provider"
                className="inline-flex items-center space-x-2 px-4 py-2 bg-cyan-500 text-slate-900 font-bold rounded-lg hover:bg-cyan-400 transition-colors text-sm"
              >
                <span>Become a Founding Provider</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-neon-purple/10 border border-neon-purple/30 mb-6">
                <Shield className="w-4 h-4 text-neon-purple" />
                <span className="text-sm text-neon-purple font-medium">Medex Group Insurance</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
                Stop <span className="text-neon-purple">Overpaying</span> for NEMT Insurance
              </h1>
              
              <p className="text-xl text-slate-400 mb-8">
                Activity-based insurance designed for NEMT. Your costs scale with actual transportation operations. 
                No large annual premiums. No massive down payments.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="#signup" className="btn-primary inline-flex items-center space-x-2">
                  <span>Get Started</span>
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a href="#how-it-works" className="btn-secondary">
                  How It Works
                </a>
                <Link to="/insurance/details" className="btn-secondary inline-flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>Full Details</span>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="glass-card p-8 border-neon-purple/30">
                <div className="text-center mb-6">
                  <div className="text-3xl font-heading font-bold text-white mb-2">
                    <span className="text-neon-green">Activity-Based</span>
                  </div>
                  <p className="text-slate-400">Custom pricing based on your operations</p>
                </div>
                <div className="text-center p-4 bg-red-500/10 rounded-lg mb-6">
                  <div className="text-2xl font-heading font-bold text-red-400 line-through mb-1">
                    $60,000/year
                  </div>
                  <p className="text-sm text-red-400/80">Traditional flat premium (5 vehicles)</p>
                </div>
                <div className="space-y-3">
                  {['No large down payments', 'Costs scale with activity', 'Fleet growth friendly'].map((item, i) => (
                    <div key={i} className="flex items-center space-x-3">
                      <Check className="w-5 h-5 text-neon-green" />
                      <span className="text-slate-300">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-medex-surface/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Traditional NEMT Insurance is <span className="text-red-400">Broken</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              You're paying for a system designed for long-haul trucking, not healthcare transportation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 border-red-500/20 hover:border-red-500/40 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                  <point.icon className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-sm text-slate-400">{point.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Savings Calculator Section */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Calculate Your <span className="text-neon-green">Savings</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              See how much you could save by switching to Activity-Based insurance.
            </p>
          </motion.div>
          
          <InsuranceCalculator onGetStarted={() => {
            document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
          }} />
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              How <span className="text-neon-purple">Activity-Based</span> Works
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Simple, transparent, and designed for how NEMT actually operates.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Connect Your Dispatch',
                description: 'Use MedexOVA software or give us read access to your existing dispatch system. We need to accurately track trip volume.',
                icon: Zap
              },
              {
                step: '02',
                title: 'We Track Active Trips',
                description: 'Our system monitors when your vehicles are on active trips. Coverage is only charged for miles driven with passengers.',
                icon: Truck
              },
              {
                step: '03',
                title: 'Pay Monthly for Usage',
                description: 'Get a simple monthly invoice based on actual trip miles. No surprises, no paying for parked vehicles.',
                icon: Calculator
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
              >
                <div className="glass-card p-8 h-full">
                  <div className="text-6xl font-heading font-bold text-neon-purple/20 mb-4">
                    {item.step}
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-neon-purple/10 border border-neon-purple/30 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-neon-purple" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-400">{item.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-neon-purple/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Important Note */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12 glass-card p-6 border-yellow-500/30 bg-yellow-500/5"
          >
            <div className="flex items-start space-x-4">
              <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-white mb-1">Software Requirement</h4>
                <p className="text-slate-400">
                  To ensure accurate trip tracking and fair pricing, providers must either use MedexOVA dispatch software 
                  or provide read-only API access to their existing dispatch system. This allows us to verify active trip 
                  miles and keep your costs as low as possible.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coverage Section */}
      <section className="py-20 bg-medex-surface/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
                Comprehensive NEMT Coverage
              </h2>
              <p className="text-lg text-slate-400 mb-8">
                Everything you need to operate legally and safely, bundled into one simple policy.
              </p>
              
              <div className="space-y-4">
                {coverageTypes.map((coverage, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 rounded-full bg-neon-green/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-neon-green" />
                    </div>
                    <span className="text-slate-300">{coverage.name}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="glass-card p-8">
                <h3 className="text-xl font-semibold text-white mb-6">Why Group Insurance?</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <Users className="w-6 h-6 text-neon-purple flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-white mb-1">Collective Buying Power</h4>
                      <p className="text-sm text-slate-400">
                        When 20+ providers join in a state, we negotiate group rates that individual operators can't access.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <Shield className="w-6 h-6 text-neon-purple flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-white mb-1">Shared Risk Pool</h4>
                      <p className="text-sm text-slate-400">
                        Claims are spread across the group, keeping rates stable even if one member has an incident.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <FileText className="w-6 h-6 text-neon-purple flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-white mb-1">Simplified Admin</h4>
                      <p className="text-sm text-slate-400">
                        One policy, one invoice, one point of contact. No juggling multiple carriers.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Learn More CTA Section */}
      <section className="py-12 bg-gradient-to-r from-neon-purple/10 via-neon-cyan/10 to-neon-purple/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8 border-neon-cyan/30 flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 rounded-2xl bg-neon-cyan/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-7 h-7 text-neon-cyan" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Want to Know Everything?</h3>
                <p className="text-slate-400 max-w-xl">
                  Learn exactly how coverage works, what's required to join, onboarding fees by fleet size, 
                  and why Medex is revolutionizing NEMT insurance.
                </p>
              </div>
            </div>
            <Link
              to="/insurance/details"
              className="btn-primary whitespace-nowrap inline-flex items-center space-x-2"
            >
              <span>Full Insurance Guide</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Signup Form Section */}
      <section id="signup" className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Become a <span className="text-cyan-400">Founding Provider</span>
            </h2>
            <p className="text-lg text-slate-400">
              Reserve your position as a Founding Provider. Limited spots available per state.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="glass-card p-8"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Company Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="Your Transport Company"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Contact Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full input-dark"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  State <span className="text-red-400">*</span>
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                >
                  <option value="">Select your state</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Fleet Size <span className="text-red-400">*</span>
                </label>
                <select
                  name="fleet_size"
                  value={formData.fleet_size}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                >
                  <option value="">Select fleet size</option>
                  <option value="1-5">1-5 vehicles</option>
                  <option value="6-15">6-15 vehicles</option>
                  <option value="16-30">16-30 vehicles</option>
                  <option value="31-50">31-50 vehicles</option>
                  <option value="50+">50+ vehicles</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Monthly Trip Volume
                </label>
                <select
                  name="monthly_trips"
                  value={formData.monthly_trips}
                  onChange={handleChange}
                  className="w-full input-dark"
                >
                  <option value="">Select volume</option>
                  <option value="<500">Less than 500</option>
                  <option value="500-1000">500-1,000</option>
                  <option value="1000-2500">1,000-2,500</option>
                  <option value="2500-5000">2,500-5,000</option>
                  <option value="5000+">5,000+</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Current Dispatch Software
                </label>
                <select
                  name="has_dispatch_software"
                  value={formData.has_dispatch_software}
                  onChange={handleChange}
                  className="w-full input-dark"
                >
                  <option value="">Do you use dispatch software?</option>
                  <option value="medexova">MedexOVA / OVA Dispatch</option>
                  <option value="other">Other software</option>
                  <option value="none">No software (manual)</option>
                </select>
              </div>
            </div>

            {formData.has_dispatch_software === 'other' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Which dispatch software do you use?
                </label>
                <input
                  type="text"
                  name="software_name"
                  value={formData.software_name}
                  onChange={handleChange}
                  className="w-full input-dark"
                  placeholder="e.g., TripMaster, RouteGenie, etc."
                />
              </div>
            )}

            {(formData.has_dispatch_software === 'other' || formData.has_dispatch_software === 'none') && (
              <div className="mb-6 p-4 bg-neon-cyan/10 border border-neon-cyan/30 rounded-lg">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="interested_in_medexova_software"
                    checked={formData.interested_in_medexova_software}
                    onChange={handleChange}
                    className="mt-1"
                  />
                  <span className="text-sm text-slate-300">
                    I'm interested in learning about MedexOVA dispatch software 
                    (required for Activity-Based insurance if you don't have compatible dispatch software)
                  </span>
                </label>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Additional Notes
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={3}
                className="w-full input-dark resize-none"
                placeholder="Any questions or specific needs?"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span>Become a Founding Provider</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            <p className="text-xs text-slate-500 text-center mt-4">
              By signing up, you agree to be contacted about Medex Group Insurance availability in your state.
            </p>
          </motion.form>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-medex-surface/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-heading font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {[
              {
                q: 'What is a Founding Provider?',
                a: 'Founding Providers are the first 20 providers per state who help launch Medex Insurance Group coverage. In exchange, you receive locked-in rates, reduced fees, and priority access to new features.'
              },
              {
                q: 'Why only 20 Founding Providers per state?',
                a: 'To maintain stable pricing and strong claims reserves, we launch coverage in phases. Each state opens with a limited number of Founding Providers before expanding to general availability.'
              },
              {
                q: 'How much can I actually save?',
                a: 'Most providers save 20-35% compared to traditional premiums. Your exact savings depend on your trip patterns, but the less idle time your fleet has, the more you save.'
              },
              {
                q: 'What happens if I have a claim?',
                a: 'Claims are handled through our streamlined process. Because we have real-time trip data, claim verification is faster and disputes are rare.'
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Yes. There are no long-term contracts. You can leave with 30 days notice, though we hope you won\'t want to once you see the savings.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6"
              >
                <h3 className="text-lg font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-slate-400">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsurancePage;
